import { passkeyLoginOptionsApi, passkeyLoginVerifyApi, passkeyRegisterOptionsApi, passkeyRegisterVerifyApi } from "@/api/services/main/auth";
import { TMePasskeySignalDto } from "@/api/services/main/auth/dtos/get-me.dto";
import { TPasskeyLoginVerifyResDto, TPasskeyRegisterVerifyResDto } from "@/api/services/main/auth/dtos/passkey.dto";
import { browserSupportsWebAuthn, startAuthentication, startRegistration } from "@simplewebauthn/browser";

// WebAuthn Signal API — not in the DOM lib shipped with our TS version yet.
type TSignalCapablePublicKeyCredential = {
  signalCurrentUserDetails: (signal: TMePasskeySignalDto) => Promise<void>;
};

export const isPasskeySupported = (): boolean => browserSupportsWebAuthn();

// Persistent hint that this browser holds a usable passkey — lets the unauthenticated, usernameless
// login page decide whether to auto-trigger the prompt. Set on register/login, cleared when the
// server list is empty.
const PASSKEY_ON_DEVICE_KEY = "has-passkey-on-device";

// Fingerprint of the last user details successfully pushed to the passkey provider. Keeps the signal
// from re-firing on every /get-me poll (the authenticated shell refetches once a minute).
const PASSKEY_SIGNALLED_KEY = "passkey-signalled-details";

export const markPasskeyOnDevice = (): void => localStorage.setItem(PASSKEY_ON_DEVICE_KEY, "1");
export const hasPasskeyOnDevice = (): boolean => !!localStorage.getItem(PASSKEY_ON_DEVICE_KEY);

export const clearPasskeyOnDevice = (): void => {
  localStorage.removeItem(PASSKEY_ON_DEVICE_KEY);
  // Drop the fingerprint too, so a passkey registered later re-signals instead of being skipped.
  localStorage.removeItem(PASSKEY_SIGNALLED_KEY);
};

const canSignalUserDetails = (): boolean => typeof PublicKeyCredential !== "undefined" && "signalCurrentUserDetails" in PublicKeyCredential;

/**
 * Pushes the current email / display name down to the OS-or-browser passkey store, so the picker stops
 * offering a stale address after メールアドレス変更 (or 氏名変更).
 *
 * The provider snapshots these labels at registration time and the RP cannot reach them any other way.
 * Best-effort by design: browsers without the Signal API (Firefox) and providers that ignore it keep the
 * old label until the passkey is deleted and re-registered.
 */
export const signalPasskeyUserDetails = async (signal: TMePasskeySignalDto): Promise<void> => {
  if (!canSignalUserDetails()) return;

  const fingerprint = JSON.stringify(signal);
  if (localStorage.getItem(PASSKEY_SIGNALLED_KEY) === fingerprint) return;

  try {
    await (PublicKeyCredential as unknown as TSignalCapablePublicKeyCredential).signalCurrentUserDetails(signal);
    localStorage.setItem(PASSKEY_SIGNALLED_KEY, fingerprint);
  } catch {
    // Never surface: a provider refusing the signal must not break the page, and the next load retries.
  }
};

/**
 * Runs the WebAuthn registration ceremony for the currently authenticated user and persists the
 * new credential on the backend. Must be called while logged in (access token attached by axios).
 */
export const registerPasskey = async (deviceName?: string): Promise<TPasskeyRegisterVerifyResDto> => {
  const optionsRes = await passkeyRegisterOptionsApi();
  const attestation = await startRegistration({ optionsJSON: optionsRes.data });
  const res = await passkeyRegisterVerifyApi({ response: attestation, deviceName });
  markPasskeyOnDevice();
  return res;
};

/**
 * Runs the usernameless WebAuthn authentication ceremony and exchanges the assertion for a session.
 */
export const loginWithPasskey = async (): Promise<TPasskeyLoginVerifyResDto> => {
  const optionsRes = await passkeyLoginOptionsApi();
  const assertion = await startAuthentication({ optionsJSON: optionsRes.data });
  const res = await passkeyLoginVerifyApi({ data: assertion });
  markPasskeyOnDevice();
  return res;
};

/** True when a WebAuthn ceremony was cancelled/aborted by the user (don't surface as an error). */
export const isPasskeyCancellation = (error: unknown): boolean => {
  return error instanceof Error && (error.name === "NotAllowedError" || error.name === "AbortError");
};

/**
 * True when the authenticator refused registration because it already holds a credential listed in excludeCredentials.
 */
export const isPasskeyAlreadyRegistered = (error: unknown): boolean => {
  return error instanceof Error && error.name === "InvalidStateError";
};
